/**
 * Drag-OUT support: make attachment rows / generated PDFs draggable out of
 * the browser into Gmail, Finder, or the desktop with a real file and a
 * clean human filename (never a storage hash).
 *
 * Chrome/Edge implement the non-standard `DownloadURL` dataTransfer type:
 *   "<mime>:<filename>:<absolute-url>"
 * The URL must be reachable WITHOUT auth headers, so callers pass a
 * token-in-query URL (see attachmentDownloadUrl). Other browsers ignore
 * DownloadURL; we also set text/uri-list + text/plain so the drop at least
 * carries a working link.
 */

/** Strip filesystem-hostile characters; collapse whitespace. */
export function sanitizeFilename(name: string): string {
    return name
        .replace(/[/\\:*?"<>|]/g, "-")
        .replace(/\s+/g, " ")
        .trim();
}

/**
 * Canonical document filename: `<DocType> <JobNumber> — <Customer>.pdf`.
 * Segments that are missing are skipped (never emit "undefined").
 *   buildDocumentFilename("Invoice", "072652639", "Sue Crockett")
 *     → "Invoice 072652639 — Sue Crockett.pdf"
 */
export function buildDocumentFilename(
    docType: string,
    jobNumber: string | null | undefined,
    customerName: string | null | undefined,
    ext = "pdf",
): string {
    const head = [docType.trim(), jobNumber?.trim()].filter(Boolean).join(" ");
    const tail = customerName?.trim();
    const base = tail ? `${head} — ${tail}` : head;
    return `${sanitizeFilename(base)}.${ext}`;
}

/** Best-effort extension from a mime type or existing filename. */
export function fileExtension(mime: string | null | undefined, fileName?: string | null): string {
    if (mime === "application/pdf") return "pdf";
    if (mime?.startsWith("image/")) return mime.slice("image/".length).replace("jpeg", "jpg");
    const m = fileName?.match(/\.([a-z0-9]{1,5})$/i);
    return m ? m[1].toLowerCase() : "pdf";
}

/**
 * Wire a dragstart event so the dragged row carries the real file.
 * `absoluteUrl` must be absolute (DownloadURL requires it) and must not
 * need auth headers.
 */
export function setDragOutData(e: React.DragEvent, opts: { url: string; filename: string; mime?: string }): void {
    const mime = opts.mime ?? "application/pdf";
    const absoluteUrl = opts.url.startsWith("http") ? opts.url : `${window.location.origin}${opts.url}`;
    const filename = sanitizeFilename(opts.filename);
    e.dataTransfer.effectAllowed = "copy";
    e.dataTransfer.setData("DownloadURL", `${mime}:${filename}:${absoluteUrl}`);
    e.dataTransfer.setData("text/uri-list", absoluteUrl);
    e.dataTransfer.setData("text/plain", absoluteUrl);
}
